import { useState, useEffect, useCallback, useRef } from 'react'
import { Radio } from 'lucide-react'
import type { WebhookEvent } from '../types'
import { relativeTime } from '../utils'

interface InlineWebhookPanelProps {
  entityId: string | null
  customerId: string | null
  onEventsChange?: (events: WebhookEvent[]) => void
}

export function InlineWebhookPanel({ entityId, customerId, onEventsChange }: InlineWebhookPanelProps) {
  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [lastPollAt, setLastPollAt] = useState<Date | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchEvents = useCallback(async (signal?: AbortSignal) => {
    if (!customerId) return
    try {
      const res = await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId)}`, signal ? { signal } : undefined)
      if (res.ok) {
        const data = (await res.json()) as WebhookEvent[]
        const filtered = data.filter(e => !entityId || e.entityId === entityId)
        setEvents(filtered)
        setLastPollAt(new Date())
        onEventsChange?.(filtered)
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
    }
  }, [customerId, entityId])

  useEffect(() => {
    setEvents([])
    setExpandedId(null)
    if (!customerId) return
    const ac = new AbortController()
    void fetchEvents(ac.signal)
    pollingRef.current = setInterval(() => { void fetchEvents() }, 5000)
    return () => {
      ac.abort()
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [customerId, entityId, fetchEvents])

  function statusBadgeStyle(status: string | undefined): { bg: string; color: string } {
    if (!status) return { bg: 'rgba(148,163,184,.15)', color: '#94a3b8' }
    const s = status.toUpperCase()
    if (s === 'PASS') return { bg: 'rgba(52,211,153,.15)', color: '#34d399' }
    if (s === 'FAIL') return { bg: 'rgba(248,113,113,.15)', color: '#f87171' }
    if (s === 'REFER' || s === 'REVIEW') return { bg: 'rgba(251,146,60,.15)', color: '#fb923c' }
    return { bg: 'rgba(251,191,36,.15)', color: '#fbbf24' }
  }

  if (!customerId) {
    return (
      <div
        className="rounded-[11px] p-3"
        style={{ border: '1px solid rgba(251,191,36,.2)', background: 'rgba(251,191,36,.04)' }}
      >
        <div className="flex items-center gap-2">
          <Radio size={11} className="text-amber-500" />
          <span className="text-[10px] font-medium text-amber-500 uppercase tracking-wide">Webhooks</span>
        </div>
        <p className="text-[10px] text-[#475569] italic mt-1">Configure credentials to receive webhooks</p>
      </div>
    )
  }

  return (
    <div
      className="rounded-[11px] overflow-hidden"
      style={{ border: '1px solid rgba(52,211,153,.2)' }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ borderBottom: '1px solid rgba(52,211,153,.15)', background: 'rgba(52,211,153,.04)' }}
      >
        {/* Pulsing dot */}
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="text-[10px] font-medium text-emerald-400 uppercase tracking-wide flex-1">
          Inline Webhooks
        </span>
        {entityId && (
          <span className="text-[9px] font-mono text-[#475569]">
            entity: {entityId.length > 20 ? `${entityId.slice(0, 20)}…` : entityId}
          </span>
        )}
        <span className="text-[9px] text-[#475569]">polling · 5s</span>
        {lastPollAt && (
          <span className="text-[9px] text-[#3f4f6b]">{relativeTime(lastPollAt)}</span>
        )}
        {events.length > 0 && (
          <span className="text-[9px] font-bold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">
            {events.length}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-2 space-y-1">
        {!entityId ? (
          <p className="text-[10px] text-[#475569] italic py-1">Make an API call to start listening for webhooks</p>
        ) : events.length === 0 ? (
          <div className="flex items-center gap-2 py-1">
            <span className="text-[10px] text-emerald-500">Listening for webhooks…</span>
            <span className="text-[9px] text-[#3f4f6b] font-mono">entity: {entityId}</span>
          </div>
        ) : (
          <>
            {[...events].sort((a, b) => b.id - a.id).map(ev => {
              const status = ev.payload?.overallStatus as string | undefined
              const isExpanded = expandedId === ev.id
              const badgeStyle = statusBadgeStyle(status)
              return (
                <div
                  key={ev.id}
                  className="rounded-[8px] cursor-pointer transition-colors hover:opacity-90"
                  style={{ border: '1px solid rgba(52,211,153,.12)', background: 'rgba(52,211,153,.04)' }}
                  onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                >
                  <div className="flex items-center gap-2 px-2 py-1">
                    <span
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: badgeStyle.bg, color: badgeStyle.color }}
                    >
                      {status ?? 'EVENT'}
                    </span>
                    <span className="text-[10px] text-[#94a3b8] font-mono flex-1 truncate">
                      {ev.payload?.function ?? ev.eventType ?? '—'}
                    </span>
                    {ev.payload?.workflowName && (
                      <span className="text-[9px] text-[#475569] font-mono hidden sm:block">
                        {ev.payload.workflowName}
                      </span>
                    )}
                    <span className="text-[9px] text-[#3f4f6b] shrink-0">
                      {relativeTime(new Date(ev.receivedAt))}
                    </span>
                  </div>
                  {ev.payload?.message && (
                    <div className="px-2 pb-1 text-[9px] text-[#475569]">
                      {String(ev.payload.message)}
                    </div>
                  )}
                  {isExpanded && (
                    <div
                      className="p-2"
                      style={{ borderTop: '1px solid rgba(52,211,153,.12)' }}
                    >
                      <pre className="text-[9px] text-[#94a3b8] whitespace-pre-wrap break-all font-mono">
                        {JSON.stringify(ev.payload, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )
            })}
            {/* Persistent listening indicator */}
            <div className="flex items-center gap-2 pt-1">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-[9px] text-emerald-500/70">Listening for more events…</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
